import classNames from "classnames/bind";
import React, { useCallback, useState } from "react";
import { Icon } from "../../Icon";
import type { TabsNavBarProps } from "../types";
import TabIndicator from "./TabIndicator";
import styles from "./TabsNavBar.module.scss";
import TextDataDisplay from "../../DataDisplay/TextDataDisplay/TextDataDisplay";

const cx = classNames.bind(styles);

const DEFAULT_ACTIVE_TAB_WIDTH = 0;
const DEFAULT_ACTIVE_TAB_LEFT_POSITION = 0;

/**
 * @param children The children of the nav element
 * @param activeIndex The currently selected Tab
 *
 * @returns The left position of the Tab Indicator in pixels
 */
const getTabIndicatorLeftPosition = (
  children: HTMLElement[],
  activeIndex: number
) => {
  // The left position of the tab indicator
  return children.slice(0, activeIndex + 1).reduce((total, child, index) => {
    const childStyle = window.getComputedStyle(child);

    // The left margin of the current child in pixels e.g. "20px" => 20
    let offset = Number(
      childStyle.marginLeft.substring(0, childStyle.marginLeft.length - 2)
    );

    if (activeIndex !== index) {
      // The left margin and width of the current child
      offset +=
        child.offsetWidth +
        Number(
          childStyle.marginRight.substring(0, childStyle.marginRight.length - 2)
        );
    }

    // The left offset of the current child plus the width of all prior children
    return (total += offset);
  }, 0);
};

const TabNavBar = ({
  tabs,
  activeKey,
  onClick,
  clamp,
  className,
  ...restProps
}: TabsNavBarProps) => {
  const [activeTabWidth, setActiveTabWidth] = useState(
    DEFAULT_ACTIVE_TAB_WIDTH
  );

  // The left value of the active Tab, used to animate the line underneath the Tabs
  const [tabIndicatorLeftPosition, setTabIndicatorLeftPosition] = useState(
    DEFAULT_ACTIVE_TAB_LEFT_POSITION
  );

  /**
   * This allows you to listen to changes in a ref or dependency array.
   * Since ref changes do not cause re-renders, this allows us to monitor the ref and update the UI when changes are necessary.
   */
  const onRefChange = useCallback(
    (node) => {
      if (node) {
        const activeIndex = tabs?.findIndex(
          ({ props: { tabKey } }) => tabKey === activeKey
        );

        const children = [...node.childNodes];

        setActiveTabWidth(children[activeIndex]?.offsetWidth);

        setTabIndicatorLeftPosition(
          getTabIndicatorLeftPosition(children, activeIndex)
        );
      }
    },
    [activeKey, tabs, activeKey]
  );

  const onTabClick = (newActiveTabKey: string) => {
    onClick(newActiveTabKey);
  };

  return (
    <div
      className={cx("tabNavBarContainer", className)}
      {...restProps}
    >
      <nav
        className={cx("tabNavBarItemsContainer")}
        ref={onRefChange}
      >
        {tabs?.map(({ props: { title, tabKey, iconVariant, isDisabled } }) => (
          <button
            key={tabKey}
            className={cx("tabNavBarItem", {
              selected: activeKey === tabKey,
            })}
            onClick={() => onTabClick(tabKey)}
            disabled={isDisabled}
            role="tab"
            aria-selected={activeKey === tabKey}
            aria-controls={`${tabKey}-tab`}
          >
            {iconVariant ? (
              <Icon
                variant={iconVariant}
                size={16}
              />
            ) : null}
            <TextDataDisplay
              value={title}
              clamp={clamp}
            />
          </button>
        ))}
      </nav>

      <TabIndicator
        activeTabWidth={activeTabWidth}
        tabIndicatorLeftPosition={tabIndicatorLeftPosition}
      />
    </div>
  );
};

export default TabNavBar;
